import { useState, useCallback, useEffect, useRef } from "react";
import { fetchProductPricing, type ProductPricingData } from "@/lib/ip_checkout";
import { supabase } from "@/lib/supabase";

// ── Types ──
export type SalesStep =
  | "loading"
  | "pricing"
  | "design"
  | "delivery"
  | "shipping_address"
  | "confirm_address"
  | "summary"
  | "payment"
  | "completed";

export interface SalesState {
  step: SalesStep;
  productSlug: string;
  quantity: number;
  printType: "front_only" | "front_back" | null;
  regularPrice: number;
  promoPrice: number | null;
  designChoice: "have_design" | "need_design" | "review_file" | null;
  designFee: number;
  deliveryMethod: "pickup" | "shipping" | null;
  shippingAddress: {
    fullName: string;
    company: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
  } | null;
  totalCents: number;
  subtotalCents: number;
  shippingCents: number;
  taxCents: number;
}

export interface SalesFlowMessage {
  id: string;
  role: "charvys";
  content: string;
  step: SalesStep;
  options?: Array<{ key: string; label: string; icon?: string }>;
  timestamp: number;
}

export interface PaymentMethod {
  key: "square" | "paypal";
  label: string;
  icon: string;
  color: string;
}

const TAX_RATE = 0;

// ── Kiosk session key (same as Kiosk uses)
const CHARVYS_PAYMENT_SESSION_KEY = "charvys_payment_session_v1";

interface CharvysPaymentSession {
  sessionId: string;
  source: "charvys" | "public_configurator";
  checkout_token: string;
  productSlug: string;
  product_family_slug: string;
  quantity: number;
  printType: string;
  designChoice: string;
  designFee: number;
  deliveryMethod: string;
  shippingAddress: Record<string, unknown> | null;
  totalCents: number;
  subtotalCents: number;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  // ── Extended fields ──
  configuration: Record<string, unknown>;
  client: Record<string, unknown>;
  artwork: Record<string, unknown>;
  approval: Record<string, unknown>;
  delivery_method: string;
}

// Generate a unique checkout token (alphanumeric, 32 chars)
function generateCheckoutToken(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let token = "chk_";
  for (let i = 0; i < 28; i++) token += chars.charAt(Math.floor(Math.random() * chars.length));
  return token;
}

export interface CharvysSalesFlowOptions {
  visitorId?: string;
  clientName?: string;
  clientPhone?: string;
  clientEmail?: string;
  productSlug?: string;
  quantity?: number;
  onComplete?: (data: { orderId: number; invoiceId: number; total: number }) => void;
  onMessagesChange?: (messages: SalesFlowMessage[]) => void;
}

export function useCharvysSalesFlow(options: CharvysSalesFlowOptions = {}) {
  const {
    visitorId = "",
    clientName = "",
    clientPhone = "",
    clientEmail = "",
    productSlug = "business-cards",
    quantity = 1000,
    onComplete,
    onMessagesChange,
  } = options;

  // ── Lazy pricing trigger ──
  // Pricing is NEVER fetched on mount. It only loads after start() is called,
  // which happens when a real visitor actually asks about a product in the chat.
  const [started, setStarted] = useState(false);
  const [requestedProduct, setRequestedProduct] = useState(productSlug);
  const [requestedQuantity, setRequestedQuantity] = useState(quantity);
  const requestedRef = useRef({ product: productSlug, quantity });

  const [pricingData, setPricingData] = useState<ProductPricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<SalesState>({
    step: "loading",
    productSlug,
    quantity,
    printType: null,
    regularPrice: 0,
    promoPrice: null,
    designChoice: null,
    designFee: 0,
    deliveryMethod: null,
    shippingAddress: null,
    totalCents: 0,
    subtotalCents: 0,
    shippingCents: 0,
    taxCents: 0,
  });
  const [messages, setMessages] = useState<SalesFlowMessage[]>([]);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [activePaymentMethod, setActivePaymentMethod] = useState<"square" | "paypal" | null>(null);
  const [orderResult, setOrderResult] = useState<{ orderId: number; invoiceId: number; total: number } | null>(null);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    onMessagesChange?.(messages);
  }, [messages, onMessagesChange]);

  // ── start(): explicit trigger — requests pricing for a specific product/quantity ──
  const start = useCallback((slug?: string, qty?: number) => {
    const product = slug && slug.trim() ? slug.trim() : requestedRef.current.product;
    const nextQty = qty && qty > 0 ? Math.floor(qty) : requestedRef.current.quantity;
    requestedRef.current = { product, quantity: nextQty };
    setRequestedProduct(product);
    setRequestedQuantity(nextQty);
    setState((prev) => ({ ...prev, productSlug: product, quantity: nextQty }));
    setStarted(true);
  }, []);

  // ── Load pricing from PHP backend — ONLY after start() (a real product query) ──
  useEffect(() => {
    if (!started) return;
    let cancelled = false;
    async function loadPricing() {
      try {
        setLoading(true);
        const result = await fetchProductPricing(requestedProduct, requestedQuantity);

        if (cancelled) return;

        if (!result.success || !result.data) {
          setError(result.error || "No se pudieron cargar los precios.");
          setLoading(false);
          return;
        }

        const pd = result.data;
        setPricingData(pd);

        const frontOnlyPrice = pd.pricing?.front_only?.price || 0;
        const frontBackPrice = pd.pricing?.front_back?.price || 0;

        const frontOnlyProductId = pd.pricing?.front_only?.product_id;
        const promoMatch = (pd.promotions || []).find(
          (p) => p.product_id === frontOnlyProductId
        );
        const promoPrice = promoMatch ? parseFloat(promoMatch.price) : null;

        setState((prev) => ({
          ...prev,
          step: "pricing",
          regularPrice: frontOnlyPrice,
          promoPrice,
        }));

        const displayName = pd.family?.displayName || "producto";
        const quantityLabel = pd.flow?.quantityLabel?.toLowerCase() || "unidades";

        let content = `Claro. Actualmente en **${displayName}**:\n\n`;
        if (frontOnlyPrice > 0) {
          content += `• ${requestedQuantity} ${quantityLabel} solo frente: **$${frontOnlyPrice.toFixed(2)}**\n`;
        }
        if (frontBackPrice > 0) {
          content += `• ${requestedQuantity} ${quantityLabel} frente y vuelta: **$${frontBackPrice.toFixed(2)}**\n`;
        }

        if (promoPrice !== null && promoMatch) {
          content += `\nTambién tenemos una promoción activa:\n`;
          content += `• ${promoMatch.title_es}: **$${promoPrice.toFixed(2)}**`;
          if (promoMatch.expires_at) {
            content += ` (válida hasta ${new Date(promoMatch.expires_at).toLocaleDateString("es-MX")})`;
          }
          content += `\n`;
        }

        content += `\n¿Las necesitas impresas solo por el frente o frente y vuelta?`;

        const msg: SalesFlowMessage = {
          id: crypto.randomUUID(),
          role: "charvys",
          content,
          step: "pricing",
          options: [
            { key: "front_only", label: "Solo frente", icon: "ri-file-line" },
            { key: "front_back", label: "Frente y vuelta", icon: "ri-file-copy-line" },
          ],
          timestamp: Date.now(),
        };
        setMessages([msg]);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError("Error al conectar con el servidor.");
        setLoading(false);
      }
    }
    loadPricing();
    return () => { cancelled = true; };
  }, [started, requestedProduct, requestedQuantity]);

  // ── Handle option selection ──
  const selectOption = useCallback(async (key: string) => {
    const currentStep = state.step;

    if (currentStep === "pricing") {
      if (key === "front_only") {
        const price = state.promoPrice ?? state.regularPrice;
        const isPromo = state.promoPrice !== null;
        const displayName = pricingData?.family?.displayName || "producto";

        const msg: SalesFlowMessage = {
          id: crypto.randomUUID(),
          role: "charvys",
          content: isPromo
            ? `¡Excelente! Con la promoción activa, tus ${displayName} te quedan en **$${price.toFixed(2)}**.\n\n¿Ya tienes tu diseño listo o necesitas que te ayudemos con el servicio de diseño?`
            : `Perfecto. Tus ${displayName}: **$${price.toFixed(2)}**.\n\n¿Ya tienes tu diseño listo o necesitas que te ayudemos con el servicio de diseño?`,
          step: "design",
          options: buildDesignOptions(pricingData),
          timestamp: Date.now(),
        };
        setState((prev) => ({
          ...prev,
          step: "design",
          printType: "front_only",
          subtotalCents: Math.round(price * 100),
        }));
        setMessages((prev) => [...prev, msg]);
      } else if (key === "front_back") {
        const fbPrice = pricingData?.pricing?.front_back?.price || state.regularPrice;
        const displayName = pricingData?.family?.displayName || "producto";

        const msg: SalesFlowMessage = {
          id: crypto.randomUUID(),
          role: "charvys",
          content: `Perfecto. Tus ${displayName} frente y vuelta: **$${fbPrice.toFixed(2)}**.\n\n¿Ya tienes tu diseño listo o necesitas que te ayudemos con el servicio de diseño?`,
          step: "design",
          options: buildDesignOptions(pricingData),
          timestamp: Date.now(),
        };
        setState((prev) => ({
          ...prev,
          step: "design",
          printType: "front_back",
          regularPrice: fbPrice,
          subtotalCents: Math.round(fbPrice * 100),
        }));
        setMessages((prev) => [...prev, msg]);
      }
    } else if (currentStep === "design") {
      const designFee = key === "need_design" ? (pricingData?.designService?.price || pricingData?.design?.serviceFee || 50) : 0;
      const designLabel = key === "need_design"
        ? `Servicio de diseño profesional: **$${designFee.toFixed(2)}**`
        : key === "have_design"
          ? "Sin servicio de diseño."
          : "Revisión de archivo sin costo.";

      setState((prev) => ({
        ...prev,
        step: "delivery",
        designChoice: key as "have_design" | "need_design" | "review_file",
        designFee,
      }));

      const msg: SalesFlowMessage = {
        id: crypto.randomUUID(),
        role: "charvys",
        content: `${designLabel}\n\n¿Prefieres recoger tu pedido en nuestra tienda o recibirlo por envío?`,
        step: "delivery",
        options: [
          { key: "pickup", label: "Recoger en tienda", icon: "ri-store-2-line" },
          { key: "shipping", label: "Envío a domicilio", icon: "ri-truck-line" },
        ],
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, msg]);
    } else if (currentStep === "delivery") {
      if (key === "pickup") {
        setState((prev) => ({
          ...prev,
          step: "summary",
          deliveryMethod: "pickup",
          shippingCents: 0,
        }));

        const msg: SalesFlowMessage = {
          id: crypto.randomUUID(),
          role: "charvys",
          content: `Recogerás tu pedido en:\n\n📍 **Imagen Printing**\n123 Main Street, Santa Rosa, CA 95401\n🕐 Lunes a Viernes: 9:00 AM - 6:00 PM\nSábados: 10:00 AM - 2:00 PM\n\nTiempo estimado de producción: **4-6 días hábiles**.\n\n¿Quieres agregar algo más a esta orden o continuamos?`,
          step: "summary",
          options: [
            { key: "continue", label: "Continuar al pago", icon: "ri-bank-card-line" },
            { key: "add_more", label: "Quiero agregar algo más", icon: "ri-add-line" },
          ],
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, msg]);
      } else if (key === "shipping") {
        setState((prev) => ({
          ...prev,
          step: "shipping_address",
          deliveryMethod: "shipping",
          shippingCents: 0,
        }));

        const msg: SalesFlowMessage = {
          id: crypto.randomUUID(),
          role: "charvys",
          content: `El envío es sin costo. Por favor, compárteme la dirección completa de entrega:\n\n• Nombre completo\n• Empresa (opcional)\n• Dirección\n• Apartamento/Suite (opcional)\n• Ciudad\n• Estado (ej. CA)\n• Código Postal (5 dígitos)\n• Teléfono`,
          step: "shipping_address",
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, msg]);
      }
    } else if (currentStep === "summary") {
      if (key === "continue") {
        await calculateAndShowPayment();
      } else if (key === "add_more") {
        const msg: SalesFlowMessage = {
          id: crypto.randomUUID(),
          role: "charvys",
          content: `Claro, ¿qué más te gustaría agregar a tu pedido? Puedes pedirme cotización de cualquier otro producto.`,
          step: "summary",
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, msg]);
      }
    }
  }, [state, pricingData]);

  // ── Process shipping address text ──
  const processAddressInput = useCallback((text: string) => {
    if (state.step !== "shipping_address") return null;

    const lines = text.split("\n").filter((l) => l.trim());
    if (lines.length < 3) {
      const msg: SalesFlowMessage = {
        id: crypto.randomUUID(),
        role: "charvys",
        content: `Necesito un poco más de información:\n\n• Nombre completo\n• Dirección\n• Ciudad, Estado, ZIP\n• Teléfono`,
        step: "shipping_address",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, msg]);
      return null;
    }

    const fullName = lines[0].trim();
    let company = "";
    let addressLine1 = "";
    let addressLine2 = "";
    let cityStateZip = "";
    let phone = "";

    let lineIdx = 1;
    if (lines[1] && !/\d/.test(lines[1]) && !lines[1].includes(",")) {
      company = lines[1].trim();
      lineIdx = 2;
    }
    if (lines[lineIdx]) addressLine1 = lines[lineIdx].trim();
    lineIdx++;
    if (lines[lineIdx] && (lines[lineIdx].toLowerCase().includes("apt") || lines[lineIdx].toLowerCase().includes("suite") || lines[lineIdx].toLowerCase().includes("unit"))) {
      addressLine2 = lines[lineIdx].trim();
      lineIdx++;
    }
    if (lines[lineIdx]) cityStateZip = lines[lineIdx].trim();
    lineIdx++;
    if (lines[lineIdx]) phone = lines[lineIdx].trim();

    const cszMatch = cityStateZip.match(/^(.+),\s*([A-Za-z]{2})\s*(\d{5})/);
    let city = "";
    let st = "";
    let zipCode = "";

    if (cszMatch) {
      city = cszMatch[1].trim();
      st = cszMatch[2].toUpperCase();
      zipCode = cszMatch[3];
    } else {
      const parts = cityStateZip.split(/[,\s]+/);
      if (parts.length >= 3) {
        city = parts.slice(0, -2).join(" ");
        st = parts[parts.length - 2].toUpperCase();
        zipCode = parts[parts.length - 1];
      }
    }

    if (st !== "CA" && st !== "CALIFORNIA") {
      const msg: SalesFlowMessage = {
        id: crypto.randomUUID(),
        role: "charvys",
        content: `Por ahora solo realizamos envíos dentro de California (CA). ¿La dirección está en California? Por favor verifica el estado.`,
        step: "shipping_address",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, msg]);
      return null;
    }

    const address = {
      fullName,
      company,
      addressLine1,
      addressLine2,
      city,
      state: "CA",
      zipCode: zipCode.slice(0, 5),
      phone,
    };

    setState((prev) => ({
      ...prev,
      step: "confirm_address",
      shippingAddress: address,
    }));

    const addrDisplay = [address.fullName, address.company, address.addressLine1, address.addressLine2, `${address.city}, ${address.state} ${address.zipCode}`, address.phone].filter(Boolean).join("\n");

    const msg: SalesFlowMessage = {
      id: crypto.randomUUID(),
      role: "charvys",
      content: `Esta es la dirección que tengo:\n\n${addrDisplay}\n\n¿Confirmas que esta dirección es correcta?`,
      step: "confirm_address",
      options: [
        { key: "confirm_address", label: "Sí, es correcta", icon: "ri-check-line" },
        { key: "fix_address", label: "Necesito corregirla", icon: "ri-edit-line" },
      ],
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, msg]);
    return address;
  }, [state.step]);

  // ── Handle confirm address ──
  const confirmAddress = useCallback((confirmed: boolean) => {
    if (state.step !== "confirm_address") return;

    if (confirmed) {
      setState((prev) => ({ ...prev, step: "summary" }));
      const addr = state.shippingAddress;
      const addrStr = addr
        ? `${addr.fullName}\n${addr.addressLine1}${addr.addressLine2 ? `, ${addr.addressLine2}` : ""}\n${addr.city}, ${addr.state} ${addr.zipCode}\n${addr.phone}`
        : "";

      const msg: SalesFlowMessage = {
        id: crypto.randomUUID(),
        role: "charvys",
        content: `Dirección confirmada ✅\n\n${addrStr}\n\nEl envío es sin costo. ¿Quieres agregar algo más a esta orden o continuamos con el pago?`,
        step: "summary",
        options: [
          { key: "continue", label: "Continuar al pago", icon: "ri-bank-card-line" },
          { key: "add_more", label: "Quiero agregar algo más", icon: "ri-add-line" },
        ],
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, msg]);
    } else {
      setState((prev) => ({ ...prev, step: "shipping_address" }));
      const msg: SalesFlowMessage = {
        id: crypto.randomUUID(),
        role: "charvys",
        content: `Entendido. Por favor compárteme la dirección corregida.`,
        step: "shipping_address",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, msg]);
    }
  }, [state.step, state.shippingAddress]);

  // ── Calculate total and show payment step ──
  const calculateAndShowPayment = useCallback(async () => {
    const subtotal = state.subtotalCents + Math.round(state.designFee * 100);
    const shipping = state.shippingCents;
    const tax = Math.round(subtotal * TAX_RATE);
    const total = subtotal + shipping + tax;

    setState((prev) => ({
      ...prev,
      step: "payment",
      subtotalCents: subtotal,
      totalCents: total,
      taxCents: tax,
    }));

    const displayName = pricingData?.family?.displayName || "producto";
    const printTypeLabel = state.printType === "front_only" ? "solo frente" : "frente y vuelta";
    const designLabel = state.designChoice === "need_design"
      ? `Servicio de diseño: $${state.designFee.toFixed(2)}`
      : state.designChoice === "review_file"
        ? "Revisión de archivo: Sin costo"
        : "Diseño: No requerido";
    const deliveryLabel = state.deliveryMethod === "pickup" ? "Recoger en tienda" : "Envío a domicilio";

    const summaryLines = [
      `• ${state.quantity} ${displayName} — ${printTypeLabel}`,
      `• ${designLabel}`,
      `• ${deliveryLabel}`,
      state.deliveryMethod === "shipping" ? "• Envío: Sin costo" : "",
    ].filter(Boolean);

    const availableMethods: PaymentMethod[] = [
      { key: "square", label: "Square", icon: "ri-layout-grid-line", color: "from-slate-500 to-slate-600" },
      { key: "paypal", label: "PayPal", icon: "ri-paypal-line", color: "from-sky-500 to-sky-600" },
    ];

    const msg: SalesFlowMessage = {
      id: crypto.randomUUID(),
      role: "charvys",
      content: `Perfecto. Tu pedido queda así:\n\n${summaryLines.join("\n")}\n\n**Total: $${(total / 100).toFixed(2)}**\n\nElige tu método de pago para completar tu pedido.`,
      step: "payment",
      options: availableMethods.map((m) => ({
        key: `pay_${m.key}`,
        label: `Pagar con ${m.label} — $${(total / 100).toFixed(2)}`,
        icon: m.icon,
      })),
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, msg]);
  }, [state, pricingData]);

  // ── Kiosk-style payment: Square ──
  const payWithSquare = useCallback(async () => {
    const subtotalCents = state.subtotalCents + Math.round(state.designFee * 100);
    const totalCents = subtotalCents + state.shippingCents + state.taxCents;
    const tempOrderNumber = `CHV-${Date.now()}`;
    const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const checkoutToken = generateCheckoutToken();

    const productFamilySlug = pricingData?.family?.slug || state.productSlug;

    // Build enriched session — includes ALL extended fields
    const session: CharvysPaymentSession = {
      sessionId,
      source: "charvys",
      checkout_token: checkoutToken,
      productSlug: state.productSlug,
      product_family_slug: productFamilySlug,
      quantity: state.quantity,
      printType: state.printType || "front_only",
      designChoice: state.designChoice || "have_design",
      designFee: state.designFee,
      deliveryMethod: state.deliveryMethod || "pickup",
      shippingAddress: state.shippingAddress || null,
      totalCents,
      subtotalCents,
      clientName: clientName || "",
      clientPhone: clientPhone || "",
      clientEmail: clientEmail || "",
      configuration: {
        product_slug: state.productSlug,
        product_family_slug: productFamilySlug,
        quantity: state.quantity,
        print_type: state.printType,
        design_choice: state.designChoice,
        design_fee: state.designFee,
      },
      client: {
        name: clientName || "",
        phone: clientPhone || "",
        email: clientEmail || "",
      },
      artwork: {},
      approval: {
        design_authorized: state.designChoice === "have_design" || state.designChoice === "review_file",
      },
      delivery_method: state.deliveryMethod || "pickup",
      shipping_address: state.shippingAddress || null,
    };
    sessionStorage.setItem(CHARVYS_PAYMENT_SESSION_KEY, JSON.stringify(session));

    // ── Save checkout session to Supabase for post-payment processing ──
    try {
      await supabase.from("checkout_sessions").insert({
        token: checkoutToken,
        client_name: clientName || "Cliente",
        client_email: clientEmail || null,
        client_phone: clientPhone || null,
        configuration_json: session.configuration,
        subtotal: totalCents / 100,
        tax: 0,
        total: totalCents / 100,
        status: "pending",
        expires_at: new Date(Date.now() + 3600000).toISOString(),
        delivery_method: state.deliveryMethod || "pickup",
        shipping_address_json: state.shippingAddress || null,
        shipping_cents: state.shippingCents,
        subtotal_cents: subtotalCents,
        tax_cents: 0,
        total_cents: totalCents,
        artwork_json: {},
      });
    } catch (e) {
      console.error("checkout_sessions insert error:", e);
      // Non-blocking: continue with payment
    }

    // Build redirect URL to success page
    const origin = window.location.origin;
    const basePath = ((window as any).__BASE_PATH__ || "").replace(/\/+$/, "");
    const redirectUrl = `${origin}${basePath}/checkout/success?squarePaid=1&source=charvys&session=${sessionId}&chk_token=${checkoutToken}`;

    // ── Build item_name and item_description ──
    const displayName = pricingData?.family?.displayName || state.productSlug;
    const printTypeLabel = state.printType === 'front_only' ? 'Front Only' : 'Front and Back';
    const charvysItemName = `${state.quantity} ${displayName} — ${printTypeLabel}`;
    const charvysItemDesc = pricingData?.family?.description || 'Standard Production';

    try {
      // Use the SAME Square edge function Kiosk uses — extra fields are harmless
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const squareRes = await fetch(
        `${supabaseUrl}/functions/v1/square-kiosk-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount_cents: totalCents,
            order_number: tempOrderNumber,
            redirect_url: redirectUrl,
            // ── Extended payload ──
            source: "charvys",
            checkout_token: checkoutToken,
            display_name: "Imagen Printing Order",
            item_name: charvysItemName,
            item_description: charvysItemDesc,
            reference_id: tempOrderNumber,
            product_family_slug: productFamilySlug,
            configuration: session.configuration,
            client: session.client,
            artwork: session.artwork,
            approval: session.approval,
            delivery_method: state.deliveryMethod || "pickup",
            shipping_address: state.shippingAddress || null,
          }),
        }
      );
      const squareData = await squareRes.json();

      if (squareData.success && squareData.payment_url) {
        setPaymentUrl(squareData.payment_url);
        setActivePaymentMethod("square");

        // Open Square in new tab (same as Kiosk)
        const newTab = window.open(squareData.payment_url, "_blank", "noopener,noreferrer");

        if (!newTab) {
          // Popup blocked — show fallback link in chat
          const msg: SalesFlowMessage = {
            id: crypto.randomUUID(),
            role: "charvys",
            content: `Tu link de pago está listo. Si la ventana no se abrió, haz clic aquí para pagar.`,
            step: "payment",
            options: [
              { key: "open_square", label: `Abrir Square — $${(totalCents / 100).toFixed(2)}`, icon: "ri-external-link-line" },
            ],
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, msg]);
        }
      } else {
        sessionStorage.removeItem(CHARVYS_PAYMENT_SESSION_KEY);
        const msg: SalesFlowMessage = {
          id: crypto.randomUUID(),
          role: "charvys",
          content: `Hubo un problema al iniciar el pago con Square: ${squareData.error || "Error desconocido"}. Intenta con otro método.`,
          step: "payment",
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, msg]);
      }
    } catch (err: any) {
      sessionStorage.removeItem(CHARVYS_PAYMENT_SESSION_KEY);
      const msg: SalesFlowMessage = {
        id: crypto.randomUUID(),
        role: "charvys",
        content: `Error de conexión al iniciar Square. Intenta con PayPal o recarga la página.`,
        step: "payment",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, msg]);
    }
  }, [state, clientName, clientPhone, clientEmail, pricingData]);

  // ── Kiosk-style payment: PayPal ──
  const payWithPayPal = useCallback(async () => {
    const subtotalCents = state.subtotalCents + Math.round(state.designFee * 100);
    const totalCents = subtotalCents + state.shippingCents + state.taxCents;
    const tempOrderNumber = `CHV-${Date.now()}`;
    const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const checkoutToken = generateCheckoutToken();

    const productFamilySlug = pricingData?.family?.slug || state.productSlug;

    // Build enriched session — includes ALL extended fields
    const session: CharvysPaymentSession = {
      sessionId,
      source: "charvys",
      checkout_token: checkoutToken,
      productSlug: state.productSlug,
      product_family_slug: productFamilySlug,
      quantity: state.quantity,
      printType: state.printType || "front_only",
      designChoice: state.designChoice || "have_design",
      designFee: state.designFee,
      deliveryMethod: state.deliveryMethod || "pickup",
      shippingAddress: state.shippingAddress || null,
      totalCents,
      subtotalCents,
      clientName: clientName || "",
      clientPhone: clientPhone || "",
      clientEmail: clientEmail || "",
      configuration: {
        product_slug: state.productSlug,
        product_family_slug: productFamilySlug,
        quantity: state.quantity,
        print_type: state.printType,
        design_choice: state.designChoice,
        design_fee: state.designFee,
      },
      client: {
        name: clientName || "",
        phone: clientPhone || "",
        email: clientEmail || "",
      },
      artwork: {},
      approval: {
        design_authorized: state.designChoice === "have_design" || state.designChoice === "review_file",
      },
      delivery_method: state.deliveryMethod || "pickup",
      shipping_address: state.shippingAddress || null,
    };
    sessionStorage.setItem(CHARVYS_PAYMENT_SESSION_KEY, JSON.stringify(session));

    // ── Save checkout session to Supabase for post-payment processing ──
    try {
      await supabase.from("checkout_sessions").insert({
        token: checkoutToken,
        client_name: clientName || "Cliente",
        client_email: clientEmail || null,
        client_phone: clientPhone || null,
        configuration_json: session.configuration,
        subtotal: totalCents / 100,
        tax: 0,
        total: totalCents / 100,
        status: "pending",
        expires_at: new Date(Date.now() + 3600000).toISOString(),
        delivery_method: state.deliveryMethod || "pickup",
        shipping_address_json: state.shippingAddress || null,
        shipping_cents: state.shippingCents,
        subtotal_cents: subtotalCents,
        tax_cents: 0,
        total_cents: totalCents,
        artwork_json: {},
      });
    } catch (e) {
      console.error("checkout_sessions insert error:", e);
      // Non-blocking: continue with payment
    }

    // Build return URLs
    const origin = window.location.origin;
    const basePath = ((window as any).__BASE_PATH__ || "").replace(/\/+$/, "");
    const returnUrl = `${origin}${basePath}/checkout/success?paypalSuccess=1&source=charvys&session=${sessionId}&chk_token=${checkoutToken}`;
    const cancelUrl = `${origin}${basePath}/checkout/success?paypalCancelled=1&source=charvys`;

    // ── Build item_name and item_description ──
    const ppDisplayName = pricingData?.family?.displayName || state.productSlug;
    const ppPrintTypeLabel = state.printType === 'front_only' ? 'Front Only' : 'Front and Back';
    const ppItemName = `${state.quantity} ${ppDisplayName} — ${ppPrintTypeLabel}`;
    const ppItemDesc = pricingData?.family?.description || 'Standard Production';

    try {
      // Use the SAME PayPal edge function Kiosk uses — extra fields are harmless
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const ppRes = await fetch(
        `${supabaseUrl}/functions/v1/paypal-kiosk-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount_cents: totalCents,
            order_number: tempOrderNumber,
            return_url: returnUrl,
            cancel_url: cancelUrl,
            // ── Extended payload ──
            source: "charvys",
            checkout_token: checkoutToken,
            display_name: "Imagen Printing Order",
            item_name: ppItemName,
            item_description: ppItemDesc,
            reference_id: tempOrderNumber,
            product_family_slug: productFamilySlug,
            configuration: session.configuration,
            client: session.client,
            artwork: session.artwork,
            approval: session.approval,
            delivery_method: state.deliveryMethod || "pickup",
            shipping_address: state.shippingAddress || null,
          }),
        }
      );
      const ppData = await ppRes.json();

      if (ppData.success && ppData.approval_url) {
        setPaymentUrl(ppData.approval_url);
        setActivePaymentMethod("paypal");

        const newTab = window.open(ppData.approval_url, "_blank", "noopener,noreferrer");

        if (!newTab) {
          const msg: SalesFlowMessage = {
            id: crypto.randomUUID(),
            role: "charvys",
            content: `Tu link de pago de PayPal está listo. Si la ventana no se abrió, haz clic aquí para pagar.`,
            step: "payment",
            options: [
              { key: "open_paypal", label: `Abrir PayPal — $${(totalCents / 100).toFixed(2)}`, icon: "ri-external-link-line" },
            ],
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, msg]);
        }
      } else {
        sessionStorage.removeItem(CHARVYS_PAYMENT_SESSION_KEY);
        const msg: SalesFlowMessage = {
          id: crypto.randomUUID(),
          role: "charvys",
          content: `Hubo un problema al iniciar el pago con PayPal: ${ppData.error || "Error desconocido"}. Intenta con otro método.`,
          step: "payment",
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, msg]);
      }
    } catch (err: any) {
      sessionStorage.removeItem(CHARVYS_PAYMENT_SESSION_KEY);
      const msg: SalesFlowMessage = {
        id: crypto.randomUUID(),
        role: "charvys",
        content: `Error de conexión al iniciar PayPal. Intenta con Square o recarga la página.`,
        step: "payment",
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, msg]);
    }
  }, [state, clientName, clientPhone, clientEmail, pricingData]);

  // ── Main payment dispatcher ──
  const handlePayNow = useCallback(async (method?: "square" | "paypal") => {
    if (!method) {
      // Show payment method options if no method specified
      await calculateAndShowPayment();
      return;
    }

    if (method === "square") {
      await payWithSquare();
    } else if (method === "paypal") {
      await payWithPayPal();
    }
  }, [payWithSquare, payWithPayPal, calculateAndShowPayment]);

  return {
    started,
    start,
    pricingData,
    loading,
    error,
    state,
    messages,
    paymentUrl,
    activePaymentMethod,
    orderResult,
    selectOption,
    processAddressInput,
    confirmAddress,
    handlePayNow,
    payWithSquare,
    payWithPayPal,
    setState,
    setMessages,
  };
}

// ── Build design options from family config ──
function buildDesignOptions(data: ProductPricingData | null): Array<{ key: string; label: string; icon?: string }> {
  const options: Array<{ key: string; label: string; icon?: string }> = [];
  const design = data?.design;

  if (!design || design.allowUpload !== false) {
    options.push({ key: "have_design", label: "Ya tengo mi diseño", icon: "ri-file-text-line" });
  }
  if (!design || design.allowService !== false) {
    options.push({ key: "need_design", label: "Necesito servicio de diseño", icon: "ri-paint-brush-line" });
  }
  if (!design || design.allowReview !== false) {
    options.push({ key: "review_file", label: "Quiero que revisen mi archivo", icon: "ri-search-eye-line" });
  }

  return options;
}

export default useCharvysSalesFlow;